import React, { useEffect, useState } from "react";
import IconButton from "@mui/material/IconButton";
import { useHistory } from "react-router-dom";
import { useNotificationStore } from "store/notificationStore";
import { getUserType } from "store/userType";
import {
  getNotificationHubConnection,
  startNotificationHubConnection,
  NEW_NOTIFICATION_METHOD,
} from "socket/notificationHub";
import NotificationDrawer from "./NotificationDrawer";
import NotificationToast from "./NotificationToast";
import { createEndpoint } from "config/apiEndpoints";
import { FetchData, PostData } from "hooks/api/useApiService";
import useToasterStore from "store/SnackBar/useToasterStore";

const RING_DURATION_MS = 600;

function Notification() {
  const notifications = useNotificationStore((state) => state.notifications);
  const setNotifications = useNotificationStore(
    (state) => state.setNotifications,
  );
  const isDrawerOpen = useNotificationStore((state) => state.isDrawerOpen);
  const openDrawer = useNotificationStore((state) => state.openDrawer);
  const closeDrawer = useNotificationStore((state) => state.closeDrawer);
  const receiveNotification = useNotificationStore(
    (state) => state.receiveNotification,
  );
  const activeToast = useNotificationStore((state) => state.activeToast);
  const isUserType = getUserType((state) => state.isUserType);
  const openToaster = useToasterStore((state) => state.openToaster);
  const history = useHistory();
  const [isRinging, setIsRinging] = useState(false);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const updateNotificationAction = {
    onSuccess: (data) => {
      if (data.data.statusCode == 200) {
        fetchNotifications();
      }
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.data?.GeneralError?.[0];
      openToaster(
        "error",
        errorMessage || "Something went wrong, Try again later!!",
      );
    },
  };

  const { mutate: updateNotificationStatus } = PostData(
    createEndpoint("updateNotificationStatus"),
    "post",
    updateNotificationAction,
  );

  const getNotificationsAction = {
    onSuccess: (data) => {
      if (data.data.statusCode == 200) {
        setNotifications(data?.data?.data);
      }
    },
    onError: () => {
      setNotifications([]);
    },
    enabled: false,
  };

  const { refetch: fetchNotifications } = FetchData(
    "getNotifications",
    createEndpoint("getNotifications"),
    getNotificationsAction,
  );

  useEffect(() => {
    fetchNotifications();
  }, []);

  // A live push means the list/unread count on the server changed — refetch
  // so the header badge updates without waiting for a manual page refresh.
  useEffect(() => {
    if (activeToast) {
      fetchNotifications();
    }
  }, [activeToast]);

  useEffect(() => {
    if (isDrawerOpen) {
      fetchNotifications();
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    if (!activeToast) {
      setIsRinging(false);
      return undefined;
    }

    setIsRinging(true);
    const timer = setTimeout(() => setIsRinging(false), RING_DURATION_MS);
    return () => clearTimeout(timer);
  }, [activeToast]);

  useEffect(() => {
    const hub = getNotificationHubConnection();
    hub.on(NEW_NOTIFICATION_METHOD, receiveNotification);
    startNotificationHubConnection();
    return () => {
      hub.off(NEW_NOTIFICATION_METHOD, receiveNotification);
    };
  }, [receiveNotification]);

  const handleNotificationClick = (notification) => {
    updateNotificationStatus({
      data: [{ notificationId: notification.id, status: true }],
    });
    closeDrawer();
    history.push(`/${isUserType?.replace(/\s/g, "")}/booking-applicants`, {
      unitName: notification.unitName,
      bookingId: notification.bookingId,
      customerId: notification.customerId,
    });
  };

  return (
    <div className="mod-notification">
      <IconButton
        className="bs-btn btn-icon noti-action"
        onClick={openDrawer}
        aria-label="Notifications"
      >
        <span
          className={`noti-icon icon icon-bell ${isRinging ? "ringing" : ""}`}
        ></span>
        {unreadCount > 0 && (
          <span className="noti-indicator noti-count">{unreadCount}</span>
        )}
      </IconButton>
      <NotificationDrawer
        open={isDrawerOpen}
        notifications={notifications}
        onClose={closeDrawer}
        onNotificationClick={handleNotificationClick}
      />
      <NotificationToast onNotificationClick={handleNotificationClick} />
    </div>
  );
}

export default Notification;
